/**
 * legalEmailsApi.ts
 * Servicio para el módulo de Correos Judiciales.
 * Conecta con el backend FastAPI (legal_emails router).
 */

import { supabase } from "@/lib/supabase";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface LegalEmail {
    id: string;
    process_id: string;
    from_email?: string;
    to_email: string;
    to_name?: string;
    to_address_proof: string;
    subject: string;
    status: "draft" | "sent" | "delivered" | "read" | "failed";
    sent_at?: string;
    delivered_at?: string;
    read_at?: string;
    body_hash: string;
    is_judicial_notification: boolean;
    resend_email_id?: string;
    process_number?: string;
    created_at: string;
    oath_accepted_at?: string;
    certificate_url?: string;
    provider?: string;
}

export interface LegalEmailAttachment {
    id: string;
    file_name: string;
    file_size?: number;
    mime_type?: string;
    file_path?: string;
}

export interface LegalEmailDetail extends LegalEmail {
    body_html: string;
    oath_statement?: string;
    attached_file_ids?: string[];
    attachments?: LegalEmailAttachment[];
    send_error?: string;
}

export interface SendEmailPayload {
    process_id: string;
    process_number: string;
    from_email?: string;
    from_name?: string;
    to_email: string;
    to_name?: string;
    to_address_proof: string;
    subject: string;
    body_html: string;
    attached_file_ids?: string[];
    oath_accepted: boolean;
    is_judicial_notification: boolean;
}

export interface SendEmailResponse {
    success: boolean;
    email_id: string;
    resend_email_id?: string;
    body_hash: string;
    sent_at: string;
    message: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

async function getAuthToken(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || "";
}

// ── API Functions ─────────────────────────────────────────────────────────────

/**
 * Envía un correo judicial y lo registra en Supabase.
 * Requiere que el usuario haya aceptado el juramento (Ley 2213 Art. 8).
 */
export async function sendLegalEmail(payload: SendEmailPayload): Promise<SendEmailResponse> {
    const token = await getAuthToken();
    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/send`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Error desconocido" }));
        throw new Error(err.detail || `Error ${res.status} al enviar correo`);
    }

    return res.json();
}

/**
 * Obtiene el historial de correos de un proceso.
 */
export async function getProcessEmails(processId: string): Promise<LegalEmail[]> {
    const token = await getAuthToken();
    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/${processId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Error desconocido" }));
        throw new Error(err.detail || `Error ${res.status} al obtener correos`);
    }

    return res.json();
}

/**
 * Obtiene el detalle completo de un correo (incluye body_html y oath_statement).
 */
export async function getEmailDetail(emailId: string): Promise<LegalEmailDetail> {
    const token = await getAuthToken();
    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/detail/${emailId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Error desconocido" }));
        throw new Error(err.detail || `Error ${res.status} al obtener detalle del correo`);
    }

    return res.json();
}

// ── Status Helpers ─────────────────────────────────────────────────────────────

export function getEmailStatusLabel(status: LegalEmail["status"]): string {
    const map: Record<LegalEmail["status"], string> = {
        draft: "Borrador",
        sent: "Enviado",
        delivered: "Entregado",
        read: "Leído",
        failed: "Fallido",
    };
    return map[status] || status;
}

export function getEmailStatusColor(status: LegalEmail["status"]): string {
    const map: Record<LegalEmail["status"], string> = {
        draft: "bg-gray-100 text-gray-600 border-gray-200",
        sent: "bg-blue-50 text-blue-700 border-blue-200",
        delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
        read: "bg-violet-50 text-violet-700 border-violet-200",
        failed: "bg-red-50 text-red-700 border-red-200",
    };
    return map[status] || "bg-gray-100 text-gray-600 border-gray-200";
}

/**
 * Calcula la fecha en que inician los términos procesales
 * (2 días hábiles después del envío — Ley 2213 Art. 8).
 * Excluye sábados y domingos.
 */
export function calcTerminoProcesal(sentAt: string): Date {
    const date = new Date(sentAt);
    let businessDaysAdded = 0;
    while (businessDaysAdded < 2) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) { // 0 = domingo, 6 = sábado
            businessDaysAdded++;
        }
    }
    return date;
}

// ── SMTP & Senders Types ──────────────────────────────────────────────────────

export interface SmtpSetupPayload {
    smtp_host: string;
    smtp_port: number;
    smtp_username: string;
    smtp_password?: string;
    smtp_from_email: string;
    smtp_from_name?: string;
}

export interface SmtpStatus {
    configured: boolean;
    is_verified: boolean;
    smtp_from_email?: string;
    smtp_from_name?: string;
}

export interface VerifiedSender {
    email: string;
    name: string;
    type: "smtp" | "resend" | "resend_system";
    is_verified: boolean;
}

// ── SMTP API Functions ────────────────────────────────────────────────────────

export async function getSmtpStatus(): Promise<SmtpStatus> {
    const token = await getAuthToken();
    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/smtp/status`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error("Error al obtener estado de SMTP");
    return res.json();
}

export async function setupSmtp(payload: SmtpSetupPayload): Promise<{ success: boolean; message: string }> {
    const token = await getAuthToken();
    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/smtp/setup`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Error conectando a SMTP" }));
        throw new Error(err.detail || "Error al configurar el servidor de correo");
    }
    return res.json();
}

export async function verifySmtp(code: string): Promise<{ success: boolean; message: string }> {
    const token = await getAuthToken();
    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/smtp/verify`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ code })
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Código inválido" }));
        throw new Error(err.detail || "Error en la verificación del código");
    }
    return res.json();
}

export async function getVerifiedSenders(): Promise<VerifiedSender[]> {
    const token = await getAuthToken();
    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/senders`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error("Error al listar remitentes autorizados");
    return res.json();
}

export async function registerSingleSender(email: string, name?: string): Promise<{ success: boolean; message: string }> {
    const token = await getAuthToken();
    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/senders/register`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email, name })
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Error registrando correo" }));
        throw new Error(err.detail || "Error al registrar el correo remitente");
    }
    return res.json();
}

export async function verifySingleSender(email: string, code: string): Promise<{ success: boolean; message: string }> {
    const token = await getAuthToken();
    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/senders/verify`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email, code })
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Código inválido" }));
        throw new Error(err.detail || "Error al verificar código");
    }
    return res.json();
}

export async function deleteSender(email: string): Promise<{ success: boolean; message: string }> {
    const token = await getAuthToken();
    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/senders/delete/${encodeURIComponent(email)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Error al eliminar el remitente" }));
        throw new Error(err.detail || "Error al eliminar el remitente");
    }
    return res.json();
}

export async function deleteLegalEmail(emailId: string): Promise<{ success: boolean; message: string }> {
    const token = await getAuthToken();
    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/delete/${emailId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Error al eliminar correo" }));
        throw new Error(err.detail || "Error al eliminar el correo");
    }
    return res.json();
}

export async function downloadEmailCertificate(emailId: string, processNumber?: string): Promise<void> {
    const token = await getAuthToken();
    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/certificate/${emailId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Error descargando certificado" }));
        throw new Error(err.detail || "No se pudo generar o descargar el acta de notificación");
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Testigo_Notificacion_${processNumber || emailId.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
}





